import { useEffect, useSyncExternalStore } from 'react'

/**
 * What is open inside the current area, for the one bar that says where you are.
 *
 * The bar has always shown the *area* — `Desk`, `Library` — and that is right until an area holds
 * more than one of something. A board is exactly that: opening *Semester 3* and being told you are
 * on the `Desk` is the bar reporting the room rather than the thing in it.
 *
 * A store of two lines rather than a context, because what reads it is one component and what writes
 * it is one component, and threading a provider through the frame for that would be more machinery
 * than the fact deserves. An area that says nothing leaves the bar exactly as it was.
 */

let name: string | null = null
const listeners = new Set<() => void>()

function subscribe(onChange: () => void): () => void {
  listeners.add(onChange)
  return () => {
    listeners.delete(onChange)
  }
}

function read(): string | null {
  return name
}

/** What the bar shows instead of the area's own name, or `null` when the area has not said. */
export function useWhatIsOpen(): string | null {
  return useSyncExternalStore(subscribe, read, () => null)
}

/**
 * Says what is open in this area, for as long as the component saying it is on the screen.
 *
 * It gives the name back on the way out, so an area that is left behind cannot leave its last
 * heading standing over the next one.
 */
export function useSayWhatIsOpen(what: string | null): void {
  useEffect(() => {
    name = what
    listeners.forEach((tell) => {
      tell()
    })
    return () => {
      name = null
      listeners.forEach((tell) => {
        tell()
      })
    }
  }, [what])
}
